import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";
import type { ActivePrompt, Prompt } from "../../types/PromptTypes";

interface PromptPayload {
    name: string;
    heading?: string | null;
    value: string;
    prompt_type: string;
    active: boolean;
    arch?: string | null;
    stlFile?: File | null;
    imageFile?: File | null;
    stlExtracted?: boolean;
    stlOriginalName?: string | null;
}

interface PromptState {
    allPrompts: Prompt[];
    activePrompts: ActivePrompt[];
    promptsPagination: {
        totalCount: number;
        currentPage: number;
        totalPages: number;
        limit: number;
    } | null;
    loading: boolean;
    analyzing: boolean;
    generatingPreviewId: number | null;
    error: string | null;
}

function upsertStoredPrompt(state: PromptState, prompt: Prompt) {
    const index = state.allPrompts.findIndex((p) => p.id === prompt.id);
    if (index !== -1) state.allPrompts[index] = prompt;
    else state.allPrompts.unshift(prompt);

    const activeIndex = state.activePrompts.findIndex((p) => p.id === prompt.id);
    if (prompt.active) {
        const next: ActivePrompt = {
            id: prompt.id,
            name: prompt.name,
            heading: prompt.heading || "",
            prompt_type: prompt.prompt_type,
            active: true,
            filter_arch: prompt.filter_arch,
            filter_version: prompt.filter_version,
            has_stl: prompt.has_stl,
            front_thumb: prompt.front_thumb,
            filter_image: prompt.filter_image,
        };
        if (activeIndex !== -1) state.activePrompts[activeIndex] = next;
        else state.activePrompts.push(next);
    } else if (activeIndex !== -1) {
        state.activePrompts.splice(activeIndex, 1);
    }
}

const initialState: PromptState = {
    allPrompts: [],
    activePrompts: [],
    promptsPagination: null,
    loading: false,
    analyzing: false,
    generatingPreviewId: null,
    error: null,
};

function toFormData(data: PromptPayload & { id?: number }) {
    const fd = new FormData();
    if (data.id != null) fd.append("id", String(data.id));
    fd.append("name", data.name);
    fd.append("heading", data.heading || "");
    fd.append("value", data.value || "");
    fd.append("description", data.value || "");
    fd.append("prompt_type", data.prompt_type || "base");
    fd.append("active", String(data.active));
    if (data.arch) fd.append("arch", data.arch);
    if (data.stlOriginalName) fd.append("stl_original_name", data.stlOriginalName);
    if (data.stlFile) fd.append("stl", data.stlFile);
    if (data.imageFile) fd.append("image", data.imageFile);
    return fd;
}

export const fetchPrompts = createAsyncThunk(
    "prompt/fetchAll",
    async (
        params: {
            page?: number;
            limit?: number;
            search?: string;
            active?: boolean;
        } | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get(`/smile-design/prompt/all`, {
                params: {
                    page: params?.page,
                    limit: params?.limit,
                    search: params?.search,
                    ...(params?.active !== undefined
                        ? { active: String(params.active) }
                        : {}),
                },
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchActivePrompts = createAsyncThunk(
    "prompt/fetchActive",
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.get(`/smile-design/prompt/active`, { withCredentials: true });
            const data = res.data.data;
            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.prompts)) return data.prompts;
            return [];
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchPromptById = createAsyncThunk(
    "prompt/fetchById",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await api.get(`/smile-design/prompt/${id}`, { withCredentials: true });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const addNewPrompt = createAsyncThunk(
    "prompt/create",
    async (data: PromptPayload, { rejectWithValue }) => {
        try {
            const res = await api.post("/smile-design/prompt/create", toFormData(data), {
                withCredentials: true,
                timeout: 300000,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const updatePrompt = createAsyncThunk(
    "prompt/update",
    async (
        data: PromptPayload & {
            id: number;
        },
        { rejectWithValue }
    ) => {
        try {
            const body =
                data.stlFile || data.imageFile
                    ? toFormData(data)
                    : {
                    id: data.id,
                    name: data.name,
                    heading: data.heading || null,
                    value: data.value || "",
                    description: data.value || "",
                    prompt_type: data.prompt_type,
                    active: data.active,
                    arch: data.arch || null,
                };
            const res = await api.put("/smile-design/prompt/update", body, {
                withCredentials: true,
                timeout: 300000,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const generateFilterPreview = createAsyncThunk(
    "prompt/generatePreview",
    async (
        data: { id: number; json?: string },
        { rejectWithValue }
    ) => {
        try {
            const res = await api.post(
                `/smile-design/prompt/${data.id}/generate-preview`,
                { json: data.json || undefined },
                { withCredentials: true, timeout: 180000 }
            );
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const deletePrompt = createAsyncThunk(
    "prompt/delete",
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/smile-design/prompt/delete/${id}`, { withCredentials: true });
            return id;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

const smilePromptSlice = createSlice({
    name: "smilePrompt",
    initialState,
    reducers: {
        clearPromptState: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPrompts.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchPrompts.fulfilled, (state, action) => {
                state.loading = false;
                const incoming: Prompt[] = action.payload.prompts ?? [];
                state.allPrompts = incoming.map((p) => {
                    const prev = state.allPrompts.find((x) => x.id === p.id);
                    if (prev?.filter_image && !p.filter_image) {
                        return { ...p, filter_image: prev.filter_image };
                    }
                    return p;
                });
                state.promptsPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchPrompts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchActivePrompts.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchActivePrompts.fulfilled, (state, action) => {
                state.loading = false;
                const incoming = Array.isArray(action.payload) ? action.payload : [];
                state.activePrompts = incoming.filter(
                    (p) => p && p.active !== false
                );
            })
            .addCase(fetchActivePrompts.rejected, (state, action) => {
                state.loading = false;
                state.activePrompts = [];
                state.error = action.payload as string;
            })

            .addCase(fetchPromptById.fulfilled, (state, action) => {
                upsertStoredPrompt(state, action.payload);
            })

            .addCase(addNewPrompt.pending, (state) => {
                state.loading = true;
                state.analyzing = true;
                state.error = null;
            })
            .addCase(addNewPrompt.fulfilled, (state, action) => {
                state.loading = false;
                state.analyzing = false;
                upsertStoredPrompt(state, action.payload);
            })
            .addCase(addNewPrompt.rejected, (state, action) => {
                state.loading = false;
                state.analyzing = false;
                state.error = action.payload as string;
            })

            .addCase(updatePrompt.pending, (state) => {
                state.loading = true;
                state.analyzing = true;
                state.error = null;
            })
            .addCase(updatePrompt.fulfilled, (state, action) => {
                state.loading = false;
                state.analyzing = false;
                upsertStoredPrompt(state, action.payload);
            })
            .addCase(updatePrompt.rejected, (state, action) => {
                state.loading = false;
                state.analyzing = false;
                state.error = action.payload as string;
            })

            .addCase(generateFilterPreview.pending, (state, action) => {
                state.generatingPreviewId = action.meta.arg.id;
                state.error = null;
            })
            .addCase(generateFilterPreview.fulfilled, (state, action) => {
                state.generatingPreviewId = null;
                upsertStoredPrompt(state, action.payload);
            })
            .addCase(generateFilterPreview.rejected, (state, action) => {
                state.generatingPreviewId = null;
                state.error = action.payload as string;
            })

            .addCase(deletePrompt.fulfilled, (state, action) => {
                state.allPrompts = state.allPrompts.filter((p) => p.id !== action.payload);
                state.activePrompts = state.activePrompts.filter((p) => p.id !== action.payload);
            });
    },
});

export const { clearPromptState } = smilePromptSlice.actions;
export default smilePromptSlice.reducer;
